import { Options as PopperOptions } from '@popperjs/core';
import Tippy from '@tippyjs/react';
import type * as CSS from 'csstype';
import { ReactElement, ReactNode, createContext, forwardRef } from 'react';

import { Style, prepareStyle, useStyles } from '@creditinfo-ui/styles';
import { getIsRenderable } from '@creditinfo-ui/utils';

export const TooltipContext = createContext(false);

// NOTE: This means the tooltip will be placed 20px away from the trigger element (to reserve enough space for the arrow element).
const DEFAULT_OFFSET = 20;
const DEFAULT_MAX_WIDTH = '200px';

const MIN_WIDTH = '75px';
const ARROW_PADDING = 20;

const popperOptions: Partial<PopperOptions> = {
	modifiers: [
		{
			name: 'arrow',
			options: { padding: ARROW_PADDING },
		},
	],
};

export interface TooltipStyleProps {
	shouldPreserveCase: boolean;
}

const tooltipStyle = prepareStyle<TooltipStyleProps>((utils, { shouldPreserveCase }) => ({
	backgroundColor: utils.colors.gray800,
	border: 'none',
	borderRadius: utils.borders.radii.basic,
	boxShadow: utils.boxShadows.basic,
	color: utils.colors.white,
	display: 'flex',
	flexDirection: 'column',
	fontSize: utils.fontSizes.caption,
	justifyContent: 'center',
	minWidth: MIN_WIDTH,
	padding: `${utils.spacings.xs} ${utils.spacings.sm}`,
	position: 'relative',
	textAlign: 'center',
	whiteSpace: 'pre-wrap',
	zIndex: utils.zIndices.tooltip,
	selectors: {
		'& .tippy-content': {
			position: 'relative',
			zIndex: 1,
		},
		'& .tippy-arrow::before': {
			borderColor: 'transparent',
			borderStyle: 'solid',
			borderWidth: utils.spacings.sm,
			content: "''",
			left: `-${utils.spacings.sm}`,
			position: 'absolute',
		},
		'&[data-placement="top"] > .tippy-arrow': { bottom: 0 },
		'&[data-placement="bottom"] > .tippy-arrow': { top: 0 },
		'&[data-placement="top"] > .tippy-arrow::before': {
			borderBottomWidth: 0,
			borderTopColor: utils.colors.gray800,
			bottom: `-${utils.spacings.sm}`,
			transformOrigin: 'center top',
		},
		'&[data-placement="bottom"] > .tippy-arrow::before': {
			borderBottomColor: utils.colors.gray800,
			borderTopWidth: 0,
			top: `-${utils.spacings.sm}`,
			transformOrigin: 'center bottom',
		},
	},
	extend: {
		condition: !shouldPreserveCase,
		style: {
			textTransform: 'uppercase',
		},
	},
}));

export interface TooltipProps {
	children: ReactElement;
	customStyle?: Style<TooltipStyleProps>;
	maxWidth?: CSS.Property.MaxWidth;
	offset?: number;
	onHide?: () => void;
	onShow?: () => void;
	shouldPreserveCase?: boolean;
	tooltip: ReactNode;
}

export const Tooltip = forwardRef<Element, TooltipProps>(
	(
		{
			children,
			customStyle,
			maxWidth = DEFAULT_MAX_WIDTH,
			offset = DEFAULT_OFFSET,
			onHide,
			onShow,
			shouldPreserveCase = false,
			tooltip,
		},
		ref
	) => {
		const { applyStyle, utils } = useStyles();

		if (!getIsRenderable(tooltip)) {
			return children;
		}

		return (
			// eslint-disable-next-line react/jsx-boolean-value
			<TooltipContext.Provider value={true}>
				<Tippy
					// NOTE: Tippy wants to call `onShow` & `onHide` even when they are undefined.
					{...(onShow && { onShow })}
					{...(onHide && { onHide })}
					animation={false}
					placement="bottom"
					content={tooltip}
					maxWidth={maxWidth}
					offset={[0, offset]}
					zIndex={utils.zIndices.tooltip}
					className={applyStyle([tooltipStyle, customStyle], { shouldPreserveCase })}
					ref={ref}
					popperOptions={popperOptions}
				>
					{children}
				</Tippy>
			</TooltipContext.Provider>
		);
	}
);
